/**
 * The one function the UI calls: raw input in, finished output out.
 *
 * Everything routes through HTML in the middle — Markdown input is converted to
 * HTML first, then cleaned, then converted to whatever the user asked for. That
 * keeps one cleaning implementation instead of three.
 */

import type { CleanOptions, InputMode, OutputFormat } from './types';
import { cleanHtml } from './clean';
import { htmlToMarkdown } from './markdown';
import { htmlToText } from './plaintext';
import { markdownToHtml } from './md-to-html';
import { tidyHtml } from './tidy';
import { sanitizeHtml } from './sanitize';

export type { CleanOptions, InputMode, OutputFormat };
export { DEFAULT_OPTIONS } from './types';
export { cleanHtml, htmlToMarkdown, htmlToText, markdownToHtml, tidyHtml, sanitizeHtml };
export { htmlToDocxBlob, DOCX_MIME } from './docx';
export { PRESETS } from './presets';
export type { Preset } from './presets';

export interface ConvertResult {
  /** The finished output in the requested format. */
  output: string;
  /** Cleaned HTML, always produced — the visual preview renders this. */
  html: string;
}

export function convertDocument(input: string, mode: InputMode, opts: CleanOptions): ConvertResult {
  const asHtml = mode === 'markdown' ? markdownToHtml(input) : input;
  const cleaned = cleanHtml(asHtml, opts);

  let output: string;
  switch (opts.format) {
    case 'markdown':
      output = htmlToMarkdown(cleaned);
      break;
    case 'text':
      output = htmlToText(cleaned);
      break;
    case 'word':
      // Word is a binary .docx built at download time — there is no text form
      // to show in the code view, so we hand back a readable plain-text version
      // for the character count and any fallback.
      output = htmlToText(cleaned);
      break;
    default:
      output = opts.tidy ? tidyHtml(cleaned) : cleaned;
      break;
  }

  return { output, html: cleaned };
}
